<!--
  StoreBaseMargin -- per-store base-margin override sub-panel.

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
  Pebble: Pricing defaults landing + per-store base margin override +
  SA global default base margin (IMPLEMENTATION-PLAN.md, 2026-06-04
  functional model).

  4-level resolver -- short-circuits on the FIRST non-null value
  going up::

    store.base_margin
    ?? distributor.default_base_margin
    ?? platform_settings['default_base_margin']  (SA-global, fetched
                                                   on mount via GET
                                                   /api/platform-settings
                                                   /default-base-margin)
    ?? 0.40                                       (hardcoded last-resort)

  Badge cases (only when no store override is set):

    1. distributor default null AND SA-global null
       -> "Using platform fallback (40%)" badge, single Override CTA.

    2. distributor default null AND SA-global set
       -> "Using platform default (X%)" badge, single Override CTA.

    3. distributor default set (regardless of SA-global)
       -> "Using distributor default (X%)" badge, single Override CTA.

    4. campaign.base_margin IS set
       -> input shows the integer percent, "Reset to distributor
       default" CTA returns getData() to {base_margin: null}.

  StoreFeatures wraps this panel in <dependent-disclosure> on
  storefrontPricingEnabled, same pattern as the other 3 price-dependent
  children (shipping / fundraising / coupons). When pricing is OFF the
  panel collapses to a single inert label and contributes nothing to
  the save payload.

  Test-id prefix: ct-base-margin-* per the implementation plan.

  Per-size margin is OUT of scope here -- it belongs on the product
  editor (separate future PR).
-->
<template>
    <section
        id="base-margin"
        class="base-margin"
        data-testid="ct-base-margin-root"
    >
        <div class="base-margin__header">
            <span class="base-margin__label">Base margin</span>
            <span
                v-if="!editing"
                class="base-margin__badge"
                data-testid="ct-base-margin-badge"
            >
                {{ badgeText }}
            </span>
        </div>

        <div v-if="!editing" class="base-margin__row">
            <button
                type="button"
                class="tf-btn tf-btn--ghost"
                :disabled="disableFields"
                data-testid="ct-base-margin-override-btn"
                @click="onOverride"
            >
                Override
            </button>
        </div>

        <div v-else class="base-margin__editor">
            <label
                class="base-margin__label"
                for="ct-base-margin-input"
            >
                Store base margin
            </label>
            <div class="base-margin__input-row">
                <input
                    id="ct-base-margin-input"
                    v-model="percentDraft"
                    type="number"
                    inputmode="decimal"
                    step="1"
                    min="0"
                    max="100"
                    :disabled="disableFields"
                    class="base-margin__input"
                    data-testid="ct-base-margin-input"
                />
                <span class="base-margin__suffix">%</span>
            </div>
            <p
                v-if="errorMsg"
                class="base-margin__error"
                data-testid="ct-base-margin-error"
            >
                {{ errorMsg }}
            </p>
            <button
                type="button"
                class="tf-btn tf-btn--ghost base-margin__reset"
                :disabled="disableFields"
                data-testid="ct-base-margin-reset-btn"
                @click="onReset"
            >
                Reset to distributor default
            </button>
        </div>
    </section>
</template>

<script>
import axios from "@/axios";

// Hardcoded last-resort fraction. Tracks the backend constant
// (_HARDCODED_FALLBACK_MARGIN in api/src/endpoints/distributor_defaults.py)
// and the matching frontend constants in DefaultsPricing.vue and
// GlobalDefaultMargin.vue.
const FALLBACK_FRACTION = 0.4;

// Endpoint that returns the SA-global default base margin (layer 3 of
// the 4-level resolver). @login_required only -- any authenticated user
// can read it; the value is non-sensitive.
const SA_GLOBAL_URL = "/api/platform-settings/default-base-margin";

export default {
    name: "StoreBaseMargin",

    props: {
        campaign: { type: Object, default: () => ({}) },
        editMode: { type: Boolean, default: false },
        disableFields: { type: Boolean, default: false },
        storefrontPricingEnabled: { type: Boolean, default: false },
    },

    emits: ["campaign-changed"],

    data() {
        return {
            // null means "no override stored" (use distributor default
            // or fallback). A number in [0, 1] is the live override.
            overrideFraction: null,
            // Draft is the integer percent the user types.
            percentDraft: "",
            errorMsg: "",
            // The SA-global default base margin (layer 3 of the
            // resolver). Fetched once on mount. Null when no SA-global
            // is set OR when the fetch fails -- in either case the
            // badge falls through to the hardcoded last-resort.
            saGlobalFraction: null,
        };
    },

    computed: {
        distributorDefaultFraction() {
            const v = this?.campaign?.distributor?.default_base_margin;
            if (v === null || v === undefined || v === "") return null;
            const f = Number(v);
            return Number.isFinite(f) ? f : null;
        },
        editing() {
            return this.overrideFraction !== null;
        },
        badgeText() {
            // 4-level chain, short-circuiting up: store override is
            // handled by the editing-branch UI; here we render the
            // "inheriting" badge text. Distributor wins over SA-global;
            // SA-global wins over hardcoded fallback.
            const dist = this.distributorDefaultFraction;
            if (dist !== null) {
                return `Using distributor default (${Math.round(dist * 100)}%)`;
            }
            if (this.saGlobalFraction !== null) {
                return `Using platform default (${Math.round(this.saGlobalFraction * 100)}%)`;
            }
            return `Using platform fallback (${Math.round(FALLBACK_FRACTION * 100)}%)`;
        },
    },

    watch: {
        campaign: {
            immediate: true,
            handler() {
                this.loadFromCampaign(this.campaign);
            },
        },
        percentDraft(val) {
            this.errorMsg = "";
            if (val === "") return;
            const n = Number(val);
            if (!Number.isFinite(n)) {
                this.errorMsg = "Enter a number between 0 and 100.";
                return;
            }
            if (n < 0 || n > 100) {
                this.errorMsg = "Margin must be between 0 and 100.";
                return;
            }
            this.overrideFraction = n / 100;
            this.$emit("campaign-changed");
        },
    },

    mounted() {
        this.fetchSaGlobal();
    },

    methods: {
        async fetchSaGlobal() {
            // Layer 3 of the chain -- the SA-global default. Fire-and-
            // forget; on any error the saGlobalFraction stays null and
            // the badge falls through to the hardcoded fallback. The
            // value is non-sensitive (a number in [0, 1]) and the
            // endpoint is @login_required only, so this works for
            // every authenticated role.
            try {
                const resp = await axios.get(SA_GLOBAL_URL);
                const raw = resp?.data?.value;
                if (raw === null || raw === undefined) {
                    this.saGlobalFraction = null;
                    return;
                }
                const f = Number(raw);
                this.saGlobalFraction = Number.isFinite(f) ? f : null;
            } catch (e) {
                this.saGlobalFraction = null;
            }
        },

        loadFromCampaign(campaign) {
            const raw = campaign?.base_margin;
            if (raw === null || raw === undefined || raw === "") {
                this.overrideFraction = null;
                this.percentDraft = "";
                return;
            }
            const f = Number(raw);
            if (!Number.isFinite(f)) {
                this.overrideFraction = null;
                this.percentDraft = "";
                return;
            }
            this.overrideFraction = f;
            this.percentDraft = String(Math.round(f * 100));
        },

        onOverride() {
            // Seed the override with whichever default the store is
            // currently inheriting so the user has a sensible starting
            // point rather than a blank input. Mirrors the badge's
            // resolver chain: distributor wins over SA-global wins over
            // hardcoded fallback.
            let seed;
            if (this.distributorDefaultFraction !== null) {
                seed = this.distributorDefaultFraction;
            } else if (this.saGlobalFraction !== null) {
                seed = this.saGlobalFraction;
            } else {
                seed = FALLBACK_FRACTION;
            }
            this.overrideFraction = seed;
            this.percentDraft = String(Math.round(seed * 100));
            this.$emit("campaign-changed");
        },

        onReset() {
            this.overrideFraction = null;
            this.percentDraft = "";
            this.errorMsg = "";
            this.$emit("campaign-changed");
        },

        getData() {
            // Persist the override as a fraction in [0, 1] or null. The
            // backend SAFRS update_campaign reads ``newCampaign["base_margin"]``;
            // explicit null is the "clear override" signal.
            if (this.overrideFraction === null) {
                return { base_margin: null };
            }
            return { base_margin: this.overrideFraction };
        },
    },
};
</script>

<style scoped>
.base-margin {
    margin-top: var(--tf-sp-3, 12px);
    padding: var(--tf-sp-3, 12px) var(--tf-sp-4, 16px);
}
.base-margin__header {
    display: flex;
    align-items: center;
    gap: var(--tf-sp-3, 12px);
    margin-bottom: var(--tf-sp-2, 8px);
}
.base-margin__label {
    font-weight: 600;
    color: var(--tf-text, #1f2937);
}
.base-margin__badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--tf-surface-muted, #f3f4f6);
    color: var(--tf-text-muted, #4b5563);
    font-size: 0.75rem;
}
.base-margin__row {
    display: flex;
    align-items: center;
}
.base-margin__editor {
    display: flex;
    flex-direction: column;
    gap: var(--tf-sp-2, 8px);
    max-width: 320px;
}
.base-margin__input-row {
    display: flex;
    align-items: center;
    gap: var(--tf-sp-2, 8px);
}
.base-margin__input {
    flex: 1;
    padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: var(--tf-radius-sm, 6px);
    font-size: var(--tf-size-body, 1rem);
    max-width: 120px;
}
.base-margin__suffix {
    color: var(--tf-text-muted, #4b5563);
}
.base-margin__error {
    color: var(--tf-error, #b91c1c);
    font-size: 0.875rem;
    margin: 0;
}
.base-margin__reset {
    align-self: flex-start;
}
.tf-btn {
    background: transparent;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: var(--tf-radius-sm, 6px);
    padding: var(--tf-sp-2, 8px) var(--tf-sp-4, 16px);
    cursor: pointer;
    color: var(--tf-text, #1f2937);
    font-size: 0.875rem;
}
.tf-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
</style>
